import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CompanyInfo } from 'src/app/shared/models/company';

export class BalanceSheetPdfGenerator {
  generatePdf(data: any, companyInfo: CompanyInfo, asOfDate: Date): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPosition = 40;

    // Header - Company Name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.company_name || 'Balance Sheet Report', margin, yPosition);
    yPosition += 25;

    // Report Subtitle
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Balance Sheet Statement', margin, yPosition);
    
    // As Of Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`As of: ${asOfDate.toLocaleDateString()}`, margin, yPosition + 15);
    
    yPosition += 50;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Summary Overview', margin, yPosition);
    yPosition += 15;

    const summaryRows = [
      ['Total Assets', `$${(data.totalAssets || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Liabilities', `$${(data.totalLiabilities || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Equity', `$${(data.totalEquity || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Amount']],
      body: summaryRows,
      margin: { left: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Assets Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Assets Breakdown', margin, yPosition);
    yPosition += 15;

    const assetRows: any[] = [];
    data.assetAccounts.forEach((group: any) => {
      assetRows.push([{ content: group.category, colSpan: 2, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
      group.items.forEach((item: any) => {
        assetRows.push([
          `  ${item.name}`,
          `$${(item.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
        ]);
      });
    });

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Total']],
      body: assetRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 168, 255] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Check if we need a new page for Liabilities
    if (yPosition > doc.internal.pageSize.getHeight() - 150) {
      doc.addPage();
      yPosition = 40;
    }

    // Liabilities & Equity Table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Liabilities & Equity Breakdown', margin, yPosition);
    yPosition += 15;

    const liabEquityRows: any[] = [];
    
    // Liabilities
    data.liabilityAccounts.forEach((group: any) => {
      liabEquityRows.push([{ content: group.category, colSpan: 2, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
      group.items.forEach((item: any) => {
        liabEquityRows.push([
          `  ${item.name}`,
          `$${(item.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
        ]);
      });
    });

    // Equity
    liabEquityRows.push([{ content: 'Equity', colSpan: 2, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
    data.equityAccounts.forEach((item: any) => {
      liabEquityRows.push([
        `  ${item.name}`,
        `$${(item.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
      ]);
    });

    autoTable(doc, {
      startY: yPosition,
      head: [['Account', 'Total']],
      body: liabEquityRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 77, 77] },
      columnStyles: {
        1: { halign: 'right' }
      }
    });

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'center' }
      );
    }

    return doc;
  }
}
